import React, { useState } from 'react';
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

const CommonVisualInsights = ({ loading, overallTotals, cohortSeriesData, timelineSeriesData, districtRiskData }) => {
    const [activeInsightTab, setActiveInsightTab] = useState("cohort");
    const [showPercentages, setShowPercentages] = useState(false);

    const commonLegend = {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
            color: "var(--text-secondary)",
            fontSize: "10px",
            fontWeight: "normal"
        }
    };

    const donutChartOptions = {
        chart: { type: "pie", backgroundColor: "transparent", style: { fontFamily: "Inter, sans-serif" }, height: 300 },
        title: { text: "Overall Anemia Status Overview", style: { color: "var(--text-primary)", fontWeight: "bold", fontSize: "14px" } },
        tooltip: { pointFormat: "<b>{point.y} cases</b> ({point.percentage:.1f}%)" },
        accessibility: { enabled: false },
        plotOptions: {
            pie: { innerSize: "60%", dataLabels: { enabled: true, format: "{point.name}: {point.percentage:.1f}%", style: { color: "var(--text-primary)", textOutline: "none", fontSize: "10px" } }, showInLegend: true }
        },
        credits: { enabled: false },
        legend: commonLegend,
        series: [{
            name: "Cases",
            data: [
                { name: "Non Anaemic", y: overallTotals?.total_normal || 0, color: "#4CAF50" },
                { name: "Mild Anemia", y: overallTotals?.total_mild || 0, color: "#FFC107" },
                { name: "Moderate Anemia", y: overallTotals?.total_moderate || 0, color: "#FF9800" },
                { name: "Severe Anemia", y: overallTotals?.total_severe || 0, color: "#F44336" }
            ]
        }]
    };

    const stackedColumnOptions = {
        chart: { type: "bar", backgroundColor: "transparent", style: { fontFamily: "Inter, sans-serif" }, height: 300 },
        title: { text: showPercentages ? "Group Vulnerability" : "Anemia Severity by Group", style: { color: "var(--text-primary)", fontWeight: "bold", fontSize: "14px" } },
        xAxis: { categories: cohortSeriesData.categories, labels: { style: { color: "var(--text-secondary)", fontSize: "9px" } }, lineColor: "var(--panel-border)", tickColor: "var(--panel-border)" },
        yAxis: { min: 0, reversedStacks: false, title: { text: showPercentages ? "Percentage (%)" : "Count of Cases", style: { color: "var(--text-secondary)" } }, labels: { style: { color: "var(--text-secondary)", fontSize: "9px" } }, gridLineColor: "var(--panel-border)" },
        tooltip: { shared: true, headerFormat: "<b>{point.key}</b><br/>", pointFormat: showPercentages ? '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.percentage:.1f}%</b> ({point.y} cases)<br/>' : '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y} cases</b><br/>' },
        plotOptions: { series: { stacking: showPercentages ? "percent" : "normal", dataLabels: { enabled: false }, borderWidth: 0 } },
        credits: { enabled: false },
        legend: commonLegend,
        series: [
            { name: "Non Anaemic", data: cohortSeriesData.nonData, color: "#4CAF50" },
            { name: "Mild Anemia", data: cohortSeriesData.mildData, color: "#FFC107" },
            { name: "Moderate Anemia", data: cohortSeriesData.modData, color: "#FF9800" },
            { name: "Severe Anemia", data: cohortSeriesData.sevData, color: "#F44336" }
        ]
    };

    const timelineLineOptions = {
        chart: { type: "area", backgroundColor: "transparent", style: { fontFamily: "Inter, sans-serif" }, height: 300 },
        title: { text: "Timeline Trend Analysis", style: { color: "var(--text-primary)", fontWeight: "bold", fontSize: "14px" } },
        xAxis: { categories: timelineSeriesData.dates, labels: { style: { color: "var(--text-secondary)", fontSize: "9px" } }, lineColor: "var(--panel-border)", tickColor: "var(--panel-border)" },
        yAxis: { min: 0, title: { text: "Count of Cases", style: { color: "var(--text-secondary)" } }, labels: { style: { color: "var(--text-secondary)", fontSize: "9px" } }, gridLineColor: "var(--panel-border)" },
        tooltip: { shared: true, headerFormat: "<b>Date: {point.key}</b><br/>", pointFormat: '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y} cases</b><br/>' },
        plotOptions: { area: { stacking: "normal", lineColor: "#ffffff", lineWidth: 1, marker: { lineWidth: 1, lineColor: "#ffffff" } } },
        credits: { enabled: false },
        legend: commonLegend,
        series: [
            { name: "Non Anaemic", data: timelineSeriesData.nonList, color: "#4CAF50" },
            { name: "Mild Anemia", data: timelineSeriesData.mildList, color: "#FFC107" },
            { name: "Moderate Anemia", data: timelineSeriesData.modList, color: "#FF9800" },
            { name: "Severe Anemia", data: timelineSeriesData.sevList, color: "#F44336" }
        ]
    };

    const districtRiskOptions = {
        chart: { type: "column", backgroundColor: "transparent", style: { fontFamily: "Inter, sans-serif" }, height: 300 },
        title: { text: "Max Severe Anemic Patients Reported by Districts", style: { color: "var(--text-primary)", fontWeight: "bold", fontSize: "14px" } },
        xAxis: { categories: districtRiskData.categories, labels: { style: { color: "var(--text-secondary)", fontSize: "9px" } }, lineColor: "var(--panel-border)", tickColor: "var(--panel-border)" },
        yAxis: { min: 0, title: { text: "Number of Affected Persons", style: { color: "var(--text-secondary)" } }, labels: { style: { color: "var(--text-secondary)", fontSize: "9px" } }, gridLineColor: "var(--panel-border)" },
        tooltip: { shared: true, headerFormat: "<b>District: {point.key}</b><br/>", pointFormat: '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y} cases</b><br/>' },
        plotOptions: { series: { stacking: "normal", borderWidth: 0 } },
        credits: { enabled: false },
        legend: commonLegend,
        series: [
            { name: "Non Anaemic", data: districtRiskData.nonSeries, color: "#4CAF50" },
            { name: "Mild Anemia", data: districtRiskData.mildSeries, color: "#FFC107" },
            { name: "Moderate Anemia", data: districtRiskData.moderateSeries, color: "#FF9800" },
            { name: "Severe Anemia", data: districtRiskData.severeSeries, color: "#F44336" }
        ]
    };

    const hasData = cohortSeriesData?.categories?.length > 0 || districtRiskData?.categories?.length > 0 || timelineSeriesData?.dates?.length > 0;

    return (
        <div className="card border-0 bg-transparent shadow-none">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div><h4 className="h6 fw-bold text-body mb-0">📊 Visual Insights</h4></div>
                <div className="d-flex gap-2 align-items-center">
                    {activeInsightTab === "cohort" && (
                        <button onClick={() => setShowPercentages(!showPercentages)} className="btn btn-sm btn-outline-info fw-semibold me-2" style={{ borderRadius: 8 }}>
                            {showPercentages ? "🔢 Show Absolute" : "📈 Show Vulnerability %"}
                        </button>
                    )}
                    <button onClick={() => setActiveInsightTab("cohort")} className={`btn btn-sm fw-semibold ${activeInsightTab === "cohort" ? "active-tab-btn" : "btn-outline-secondary"}`} style={{ borderRadius: 8 }}>
                        👥 Group Wise Breakdown
                    </button>
                    {/* <button onClick={() => setActiveInsightTab("timeline")} className={`btn btn-sm fw-semibold ${activeInsightTab === "timeline" ? "active-tab-btn" : "btn-outline-secondary"}`} style={{ borderRadius: 8 }}>
                        📅 Timeline Trend
                    </button> */}
                    <button onClick={() => setActiveInsightTab("risk")} className={`btn btn-sm fw-semibold ${activeInsightTab === "risk" ? "active-tab-btn" : "btn-outline-secondary"}`} style={{ borderRadius: 8 }}>
                        🚩 Anemia Status (District Level)
                    </button>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-lg-4 col-md-12">
                    <div className="glass-card card p-3 h-100" style={{ borderRadius: 16 }}>
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <div className="placeholder-glow w-100 text-center"><div className="placeholder rounded-circle" style={{ width: 220, height: 220 }}></div></div>
                            </div>
                        ) : !hasData ? (
                            <div className="text-center py-5 text-muted">No data available for health status</div>
                        ) : (
                            <HighchartsReact highcharts={Highcharts} options={donutChartOptions} />
                        )}
                    </div>
                </div>

                <div className="col-lg-8 col-md-12">
                    <div className="glass-card card p-3 h-100" style={{ borderRadius: 16 }}>
                        {loading ? (
                            <div className="d-flex flex-column h-100 py-3">
                                <div className="placeholder-glow mb-4"><span className="placeholder col-4 rounded" style={{ height: 20 }}></span></div>
                                <div className="d-flex align-items-end flex-grow-1 gap-3 placeholder-glow" style={{ minHeight: 200 }}>
                                    {[...Array(8)].map((_, i) => (<div key={i} className="placeholder rounded w-100" style={{ height: `${Math.max(30, Math.random() * 100)}%` }}></div>))}
                                </div>
                            </div>
                        ) : !hasData ? (
                            <div className="text-center py-5 text-muted">No data available for group analysis</div>
                        ) : activeInsightTab === "cohort" ? (
                            <HighchartsReact highcharts={Highcharts} options={stackedColumnOptions} />
                        ) : activeInsightTab === "timeline" ? (
                            <HighchartsReact highcharts={Highcharts} options={timelineLineOptions} />
                        ) : (
                            <HighchartsReact highcharts={Highcharts} options={districtRiskOptions} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CommonVisualInsights;
